import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import CasosAbiertosModal from "../components/CasosAbiertosModal";

import { CentinelaAvatar } from "../../../components/base/CentinelaChat";
import { Button, Card, EmptyState, SectionHeader, Spinner } from "../../../components/ui";
import { useCentinelaSummary } from "../../../features/centinela/queries";
import { useTripsList } from "../../../features/trips/queries";
import { usePrefersReducedMotion } from "../../../hooks/usePrefersReducedMotion";
import CentinelaBoot, { BOOT_HEIGHT } from "../components/CentinelaBoot";
import { claimCentinelaBoot } from "../components/bootVisit";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import TabLoader from "../components/TabLoader";
import { useWatchedFleet } from "../hooks/useWatchedFleet";
import { mapCentinelaSummary } from "../services/mapCentinelaSummary";
import type { MonitoringCustody, MonitoringTripCounts } from "../services/mapCentinelaSummary";
import { NARRATIVE_CARD_CLASSES, WATCH_STATUS_CHART_COLORS } from "../theme";
import { WATCH_STATUSES } from "../types";

const DONUT_RADIUS = 15.9;
const DONUT_CIRCUMFERENCE = 100;

const MORPH_MS = 700;

type BootPhase = "booting" | "morphing" | "done";

const PERFORMANCE_TILE_STYLES = {
  answerRate: { icon: "ri-check-double-line", tone: "text-success" },
  resolvedWithoutCall: { icon: "ri-shield-check-line", tone: "text-info" },
  requiresHuman: { icon: "ri-user-voice-line", tone: "text-warning" },
  successRate: { icon: "ri-check-double-line", tone: "text-success" },
  reactionTime: { icon: "ri-timer-flash-line", tone: "text-info" },
  realIncidents: { icon: "ri-shield-check-line", tone: "text-content-secondary" },
} as const satisfies Record<string, { icon: string; tone: string }>;

const TRIP_STATUS_BARS = [
  { id: "inProgress", bar: "bg-success" },
  { id: "scheduled", bar: "bg-info" },
  { id: "completed", bar: "bg-primary-500" },
  { id: "cancelled", bar: "bg-error" },
  { id: "unknown", bar: "bg-content-muted" },
] as const satisfies readonly { id: keyof Omit<MonitoringTripCounts, "total">; bar: string }[];

interface PerformanceTile {
  id: string;
  label: string;
  value: string;
  note: string;
  icon: string;
  tone: string;
}

function SeriesLoading() {
  const { t } = useTranslation("common");

  return (
    <div className="flex h-[90px] items-center justify-center gap-2 text-[11px] text-content-faint">
      <Spinner size="sm" />
      {t("monitoreoIa.panel.loading")}
    </div>
  );
}

function UnavailableSeries({ reason }: { reason: string | null }) {
  const { t } = useTranslation("common");

  return (
    <div className="flex h-[90px] flex-col items-center justify-center gap-1 rounded-xl bg-subtle/60 px-3 text-center">
      <span className="text-[22px] font-extrabold leading-none text-content-muted">—</span>
      <span className="text-[11px] font-medium text-content-secondary">
        {t("monitoreoIa.panel.unavailable")}
      </span>
      <span className="text-[10px] leading-snug text-content-faint">
        {reason ?? t("monitoreoIa.panel.unavailableHint")}
      </span>
    </div>
  );
}

export default function ResumenSection() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();

  const { units, breakdown, countOf, isLoading, isRefreshing, refresh } = useWatchedFleet();
  const summaryQuery = useCentinelaSummary();
  const trips = useTripsList({ limit: 100 });

  const prefersReducedMotion = usePrefersReducedMotion();
  const [bootPhase, setBootPhase] = useState<BootPhase>(() =>
    claimCentinelaBoot() && !prefersReducedMotion ? "booting" : "done",
  );
  const [bootKey, setBootKey] = useState(0);
  const narrativeCardRef = useRef<HTMLDivElement>(null);
  const [narrativeCardHeight, setNarrativeCardHeight] = useState(0);

  const finishBoot = () => {
    setBootPhase("morphing");
    setTimeout(() => setBootPhase("done"), MORPH_MS);
  };

  const replayBoot = () => {
    setBootKey((key) => key + 1);
    setBootPhase("booting");
  };

  const overview = useMemo(() => mapCentinelaSummary(summaryQuery.data), [summaryQuery.data]);

  /** Casos que Centinela ya no puede resolver solo. Decide si el saludo ofrece salida. */
  const casosQueEsperan = overview.cases.requiresHuman ?? 0;
  const [verCasos, setVerCasos] = useState(false);
  const summaryPending = summaryQuery.isPending && !summaryQuery.isError;
  const narrative = overview.narrative;

  const panel = overview.panel;
  const panelReady = panel.hasPanel;

  const tripRows = trips.data?.data ?? [];

  const escortedTrips = panel.custodies.length;

  const generatedAt =
    overview.generatedAt ?? (summaryQuery.dataUpdatedAt > 0 ? summaryQuery.dataUpdatedAt : null);

  const assistantName = t("monitoreoIa.assistantName");

  const narrativeTitle =
    narrative && narrative.title.length > 0
      ? narrative.title
      : t("monitoreoIa.summary.title", { name: assistantName });

  const narrativeBody = narrative ? narrative.body : t("monitoreoIa.summary.unavailable");

  useLayoutEffect(() => {
    if (narrativeCardRef.current) setNarrativeCardHeight(narrativeCardRef.current.offsetHeight);
  }, [narrativeTitle, narrativeBody]);

  const numberFormat = (value: number) => value.toLocaleString(i18n.language);

  const shortTime = (value: string | number) =>
    new Date(value).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });

  const shortDate = (value: string | number) =>
    new Date(value).toLocaleDateString(i18n.language, {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });

  const dayLabel = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, { weekday: "short" });

  const isToday = (iso: string) =>
    new Date(iso).toDateString() === new Date().toDateString();

  const alertBars = panel.alerts.points.map((point) => ({
    date: point.date,
    label: dayLabel(point.date),
    count: point.value,
    isToday: isToday(point.date),
  }));
  const maxAlerts = Math.max(...alertBars.map((point) => point.count), 1);

  const kilometerBars = panel.kilometers.points.map((point) => ({
    date: point.date,
    label: dayLabel(point.date),
    km: point.value,
    isToday: isToday(point.date),
  }));
  const maxKilometers = Math.max(...kilometerBars.map((point) => point.km), 1);

  const watchedTotal = WATCH_STATUSES.reduce((total, status) => total + breakdown[status], 0);
  const donutSegments = WATCH_STATUSES.reduce<
    { status: (typeof WATCH_STATUSES)[number]; length: number; offset: number }[]
  >((segments, status) => {
    const value = breakdown[status];
    if (value === 0) return segments;

    const previous = segments.reduce((total, segment) => total + segment.length, 0);
    return [
      ...segments,
      {
        status,
        length: (value / Math.max(watchedTotal, 1)) * DONUT_CIRCUMFERENCE,
        offset: -previous,
      },
    ];
  }, []);

  const tripBuckets = TRIP_STATUS_BARS.map((bucket) => ({
    ...bucket,
    label: t(`monitoreoIa.trips.statuses.${bucket.id}`),
    count: panel.trips[bucket.id],
  }));
  const maxTripCount = Math.max(...tripBuckets.map((bucket) => bucket.count), 1);

  const unitNameByDeviceId = useMemo(() => {
    const entries = units.flatMap((unit) =>
      (unit.devices ?? []).map((device) => [device.id, unit.name] as const),
    );
    return new Map(entries);
  }, [units]);

  const escortedList = panel.custodies.slice(0, 4).map((custody) => ({
    custody,
    trip: tripRows.find((trip) => trip.id === custody.tripId) ?? null,
    unitName: unitNameByDeviceId.get(custody.deviceId) ?? null,
  }));

  const custodyDetail = (custody: MonitoringCustody, unitName: string | null) =>
    [
      unitName,
      custody.profile,
      custody.progressPercent === null
        ? null
        : t("monitoreoIa.custody.percent", { percent: custody.progressPercent }),
      custody.progressPercent !== null && custody.progressIsEstimated
        ? t("monitoreoIa.custody.estimated")
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const iaPerformance = panel.performance;

  const callTiles: PerformanceTile[] = overview.hasData
    ? [
        {
          id: "answerRate",
          label: t("monitoreoIa.performance.answerRate"),
          value: `${overview.calls.answerRate}%`,
          note: t("monitoreoIa.performance.callsRatio", {
            done: overview.calls.answered,
            total: overview.calls.total,
          }),
          ...PERFORMANCE_TILE_STYLES.answerRate,
        },
      ]
    : [];

  const caseTiles: PerformanceTile[] = overview.hasData
    ? [
        {
          id: "resolvedWithoutCall",
          label: t("monitoreoIa.performance.resolvedWithoutCall"),
          value: numberFormat(overview.cases.resolvedWithoutCall),
          note: t("monitoreoIa.performance.ofCases", { total: overview.cases.total }),
          ...PERFORMANCE_TILE_STYLES.resolvedWithoutCall,
        },
        {
          id: "requiresHuman",
          label: t("monitoreoIa.performance.requiresHuman"),
          value: numberFormat(overview.cases.requiresHuman),
          note: t("monitoreoIa.performance.openCases", { count: overview.cases.open }),
          ...PERFORMANCE_TILE_STYLES.requiresHuman,
        },
      ]
    : [];

  const panelTiles: PerformanceTile[] = iaPerformance
    ? [
        {
          id: "successRate",
          label: t("monitoreoIa.performance.successRate"),
          value: `${iaPerformance.checks.pct}%`,
          note: t("monitoreoIa.performance.checksRatio", {
            done: iaPerformance.checks.ok,
            total: iaPerformance.checks.total,
          }),
          ...PERFORMANCE_TILE_STYLES.successRate,
        },
        ...(iaPerformance.reactionSeconds === null
          ? []
          : [
              {
                id: "reactionTime",
                label: t("monitoreoIa.performance.reactionTime"),
                value: `${iaPerformance.reactionSeconds} s`,
                note: t("monitoreoIa.performance.reactionWindow", { count: panel.windowDays }),
                ...PERFORMANCE_TILE_STYLES.reactionTime,
              },
            ]),
        {
          id: "realIncidents",
          label: t("monitoreoIa.performance.realIncidents"),
          value: numberFormat(iaPerformance.realIncidents),
          note: t("monitoreoIa.performance.windowDays", { count: panel.windowDays }),
          ...PERFORMANCE_TILE_STYLES.realIncidents,
        },
      ]
    : [];

  const performanceTiles = [...callTiles, ...panelTiles, ...caseTiles];

  const kpis = [
    { id: "safeUnits", label: t("monitoreoIa.kpis.safeUnits"), value: isLoading ? null : countOf("safe"), icon: "ri-shield-check-line", tone: "text-success", route: "unidades" },
    { id: "warnings", label: t("monitoreoIa.kpis.warnings"), value: isLoading ? null : countOf("warning"), icon: "ri-alert-line", tone: "text-warning", route: "unidades" },
    { id: "activeAlerts", label: t("monitoreoIa.kpis.activeAlerts"), value: isLoading ? null : countOf("alert"), icon: "ri-error-warning-line", tone: "text-error", route: "unidades" },
    {
      id: "callsMade",
      label: overview.hasData
        ? t("monitoreoIa.kpis.callsWindow", { hours: overview.windowHours })
        : t("monitoreoIa.kpis.callsMade"),
      value: summaryPending ? null : overview.calls.total,
      icon: "ri-phone-line",
      tone: "text-primary-500",
      route: "configurar",
    },
    {
      id: "activeGeofences",
      label: t("monitoreoIa.kpis.activeGeofences"),
      value: summaryPending ? null : overview.geofences,
      icon: "ri-map-pin-5-line",
      tone: "text-info",
      route: "geocercas",
    },
    {
      id: "escortedTrips",
      label: t("monitoreoIa.kpis.escortedTrips"),
      value: summaryPending ? null : overview.trips,
      icon: "ri-road-map-line",
      tone: "text-primary-500",
      route: "viajes",
    },
  ];

  return (
    <div className="flex h-full w-full min-w-0 flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.resumen.title")}
        subtitle={t("monitoreoIa.resumen.subtitle")}
        actions={
          <>
            {bootPhase === "done" && !prefersReducedMotion && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                onClick={replayBoot}
                title={t("monitoreoIa.boot.replay")}
                aria-label={t("monitoreoIa.boot.replay")}
                icon={<i className="ri-restart-line" />}
              />
            )}
            <RefreshSectionButton
              loading={isRefreshing || summaryQuery.isFetching}
              onRefresh={() => {
                refresh();
                void summaryQuery.refetch();
              }}
            />
          </>
        }
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        <div className="mx-auto w-full min-w-0 max-w-6xl space-y-5">
          <div
            className="relative"
            style={{
              height:
                bootPhase === "booting"
                  ? BOOT_HEIGHT
                  : bootPhase === "morphing"
                    ? narrativeCardHeight || undefined
                    : undefined,
              transition: `height ${MORPH_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              overflow: bootPhase === "done" ? undefined : "hidden",
            }}
          >
            <div
              ref={narrativeCardRef}
              className={`rounded-2xl border bg-gradient-to-r to-card p-5 transition-opacity duration-500 md:p-6 ${
                NARRATIVE_CARD_CLASSES[narrative?.tone ?? "info"]
              }`}
              style={{ opacity: bootPhase === "booting" ? 0 : 1 }}
            >
              <div className="flex flex-col items-start gap-5 md:flex-row">
                <CentinelaAvatar size="xl" pulse />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-bold text-content">
                      {summaryPending ? t("monitoreoIa.summary.title", { name: assistantName }) : narrativeTitle}
                    </h2>
                  </div>
                  {summaryPending ? (
                    <p className="mt-1.5 flex items-center gap-2 text-[13px] text-content-faint">
                      <Spinner size="sm" />
                      {t("monitoreoIa.summary.loading")}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-[13px] leading-relaxed text-content-secondary">
                      {narrativeBody}
                    </p>
                  )}
                  {/* ── El atajo a lo que espera ────────────────────────────
                      Centinela dice «necesita que alguien lo vea» y hasta
                      ahora no había por dónde ir a verlo: había que cambiar de
                      sección, encontrar el caso en una lista y recordar de cuál
                      hablaba el saludo. Un aviso sin salida enseña a leerlo
                      como decoración.

                      Abre los casos abiertos de toda la flota en un modal,
                      donde se leen y se cierran sin salir de aquí. No lleva al
                      Centro de monitoreo: cambiar de sección para cerrar tres
                      casos son tres viajes por la misma pantalla, y el saludo
                      tampoco dice de cuál habla —elegirlo por él sería
                      adivinar—. */}
                  {!summaryPending && casosQueEsperan > 0 ? (
                    <button
                      type="button"
                      onClick={() => setVerCasos(true)}
                      className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-xl border border-warning/40 max-lg:min-h-10 bg-warning/10 px-3 py-2 text-[12px] font-semibold text-warning transition-colors hover:bg-warning/20"
                    >
                      <i className="ri-user-follow-line text-[12px]" />
                      {t("monitoreoIa.summary.verLosQueEsperan", {
                        defaultValue: "Ver los casos que me esperan",
                        count: casosQueEsperan,
                      })}
                      <span className="rounded-full bg-warning/20 px-1.5 font-mono text-[10.5px]">
                        {casosQueEsperan}
                      </span>
                    </button>
                  ) : null}

                  {summaryPending || generatedAt === null ? null : (
                    <p className="mt-2 text-xs text-content-faint">
                      {t("monitoreoIa.summary.generatedAt", {
                        date: shortDate(generatedAt),
                        time: shortTime(generatedAt),
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {bootPhase !== "done" && (
              <CentinelaBoot key={bootKey} narrative={narrative} onDone={finishBoot} />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {kpis.map((kpi) => (
              <button
                key={kpi.id}
                type="button"
                onClick={() => navigate(`/monitoreo-ia/${kpi.route}`)}
                className="min-w-0 rounded-xl border border-line bg-card p-4 text-left transition-colors hover:border-primary-500/40 hover:bg-subtle"
              >
                <span className="mb-1 flex items-center gap-2">
                  <i className={`${kpi.icon} text-sm text-content-faint`} />
                  <span className="min-w-0 truncate text-[11px] text-content-secondary">{kpi.label}</span>
                </span>
                <span className={`block truncate text-[22px] font-extrabold tabular-nums max-sm:text-xl ${kpi.tone}`}>
                  {kpi.value === null ? "—" : numberFormat(kpi.value)}
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-[repeat(2,minmax(0,1fr))]">
            <Card>
              <div className="flex items-baseline gap-2">
                <h3 className="text-[13px] font-bold text-content">
                  {t("monitoreoIa.alertHistory.title")}
                </h3>
                <span className="ml-auto text-[19px] font-extrabold tabular-nums text-error">
                  {panelReady && panel.alerts.available
                    ? numberFormat(panel.alerts.total)
                    : "—"}
                </span>
              </div>
              <p className="mb-3 text-[11px] text-content-secondary">
                {t("monitoreoIa.alertHistory.subtitleDays", { count: panel.windowDays })}
              </p>
              {summaryPending ? (
                <SeriesLoading />
              ) : !panelReady || !panel.alerts.available ? (
                <UnavailableSeries reason={panel.alerts.reason} />
              ) : alertBars.length === 0 ? (
                <EmptyState icon="ri-notification-3-line" title={t("monitoreoIa.panel.noSeries")} />
              ) : (
                <div className="max-w-full overflow-x-auto"><div className="flex h-[90px] min-w-[320px] items-end gap-2 px-1">
                  {alertBars.map((point) => (
                    <div key={point.date} className="flex flex-1 flex-col items-center gap-1.5">
                      <div
                        className={`min-h-[8px] w-full rounded-t-md ${
                          point.isToday ? "bg-error" : "bg-error/30"
                        }`}
                        style={{ height: `${Math.max((point.count / maxAlerts) * 100, 8)}%` }}
                      />
                      <span
                        className={`text-[10px] font-medium ${
                          point.isToday ? "text-error" : "text-content-secondary"
                        }`}
                      >
                        {point.label}
                      </span>
                    </div>
                  ))}
                    </div>
                </div>
              )}
            </Card>

            <Card>
              <div className="flex items-baseline gap-2">
                <h3 className="text-[13px] font-bold text-content">
                  {t("monitoreoIa.fleetStatus.title")}
                </h3>
                <span className="ml-auto text-[15px] font-extrabold tabular-nums text-content">
                  {numberFormat(watchedTotal)}
                </span>
              </div>
              <p className="mb-3 text-[11px] text-content-secondary">
                {t("monitoreoIa.fleetStatus.subtitle")}
              </p>

              {watchedTotal === 0 ? (
                <EmptyState
                  icon="ri-truck-line"
                  title={t("monitoreoIa.fleetStatus.empty")}
                  description={t("monitoreoIa.fleetStatus.emptyDescription")}
                />
              ) : (
                <div className="flex items-center gap-4">
                  <div className="relative h-20 w-20 shrink-0">
                    <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                      <circle
                        cx="18"
                        cy="18"
                        r={DONUT_RADIUS}
                        fill="none"
                        stroke="rgb(var(--c-subtle))"
                        strokeWidth="4"
                      />
                      {donutSegments.map((segment) => (
                        <circle
                          key={segment.status}
                          cx="18"
                          cy="18"
                          r={DONUT_RADIUS}
                          fill="none"
                          stroke={WATCH_STATUS_CHART_COLORS[segment.status]}
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeDasharray={`${segment.length} ${DONUT_CIRCUMFERENCE}`}
                          strokeDashoffset={segment.offset}
                        />
                      ))}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[15px] font-extrabold text-content">
                        {numberFormat(countOf("safe"))}
                      </span>
                      <span className="text-[8px] text-content-secondary">
                        {t("monitoreoIa.status.safe")}
                      </span>
                    </div>
                  </div>

                  <ul className="flex flex-col gap-1.5 text-[11px]">
                    {WATCH_STATUSES.map((status) => (
                      <li key={status} className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-sm"
                          style={{ background: WATCH_STATUS_CHART_COLORS[status] }}
                        />
                        <span className="text-content-secondary">
                          {t(`monitoreoIa.status.${status}`)}{" "}
                          <b className="text-content">{numberFormat(breakdown[status])}</b>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-[repeat(2,minmax(0,1fr))]">
            <Card>
              <div className="flex items-center gap-2">
                <h3 className="text-[13px] font-bold text-content">
                  {t("monitoreoIa.kilometers.title")}
                </h3>
                <span className="ml-auto text-[19px] font-extrabold tabular-nums text-info">
                  {panelReady && panel.kilometers.available
                    ? `${numberFormat(panel.kilometers.total)} km`
                    : "—"}
                </span>
              </div>
              <p className="mb-3 text-[11px] text-content-secondary">
                {t("monitoreoIa.kilometers.rangeDays", { count: panel.windowDays })}
              </p>

              {summaryPending ? (
                <SeriesLoading />
              ) : !panelReady || !panel.kilometers.available ? (
                <UnavailableSeries reason={panel.kilometers.reason} />
              ) : kilometerBars.length === 0 ? (
                <EmptyState icon="ri-route-line" title={t("monitoreoIa.panel.noSeries")} />
              ) : (
                <div className="max-w-full overflow-x-auto"><div className="flex h-[90px] min-w-[320px] items-end gap-2 px-1">
                  {kilometerBars.map((point) => (
                    <div key={point.date} className="flex flex-1 flex-col items-center gap-1.5">
                      <div
                        className={`min-h-[8px] w-full rounded-t-md ${
                          point.isToday ? "bg-info" : "bg-info/30"
                        }`}
                        style={{ height: `${Math.max((point.km / maxKilometers) * 100, 8)}%` }}
                      />
                      <span
                        className={`text-[10px] font-medium ${
                          point.isToday ? "text-info" : "text-content-secondary"
                        }`}
                      >
                        {point.label}
                      </span>
                    </div>
                  ))}
                    </div>
                </div>
              )}
            </Card>

            <Card>
              <div className="flex items-baseline gap-2">
                <h3 className="text-[13px] font-bold text-content">
                  {t("monitoreoIa.trips.title")}
                </h3>
                <span className="ml-auto text-[19px] font-extrabold tabular-nums text-primary-500">
                  {panelReady ? numberFormat(panel.trips.total) : "—"}
                </span>
              </div>
              <p className="mb-3 text-[11px] text-content-secondary">
                {summaryPending
                  ? t("monitoreoIa.panel.loading")
                  : t("monitoreoIa.trips.subtitle", { count: escortedTrips })}
              </p>

              {summaryPending ? (
                <SeriesLoading />
              ) : !panelReady ? (
                <UnavailableSeries reason={null} />
              ) : (
                <div className="space-y-3">
                  {tripBuckets.map((bucket) => (
                    <div key={bucket.id}>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="truncate text-content-secondary">{bucket.label}</span>
                        <span className="font-bold text-content">
                          {numberFormat(bucket.count)}
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-subtle">
                        <div
                          className={`h-full rounded-full ${bucket.bar}`}
                          style={{
                            width:
                              bucket.count === 0
                                ? "0%"
                                : `${Math.max((bucket.count / maxTripCount) * 100, 4)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-xs">
                <span className="text-content-secondary">
                  {t("monitoreoIa.trips.availableRoutes")}
                </span>
                <span className="font-bold text-content">
                  {panelReady ? numberFormat(panel.availableRoutes) : "—"}
                </span>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-[repeat(2,minmax(0,1fr))]">
            <Card>
              <h3 className="text-[13px] font-bold text-content">
                {t("monitoreoIa.performance.title")}
              </h3>
              <p className="mb-4 text-[11px] text-content-secondary">
                {overview.hasData
                  ? t("monitoreoIa.performance.window", { hours: overview.windowHours })
                  : t("monitoreoIa.performance.subtitle")}
              </p>

              {summaryPending ? (
                <TabLoader label={t("monitoreoIa.performance.loading")} />
              ) : (
                <div className="space-y-3">
                  {performanceTiles.map((tile) => (
                    <div
                      key={tile.id}
                      className="flex items-center justify-between gap-3 rounded-xl bg-surface p-3"
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] text-content-secondary">{tile.label}</p>
                        <p className={`text-[22px] font-extrabold tabular-nums ${tile.tone}`}>
                          {tile.value}
                        </p>
                      </div>
                      <div className="shrink-0 text-right max-sm:max-w-[45%]">
                        <p className="text-[11px] text-content-secondary">{tile.note}</p>
                        <i className={`${tile.icon} ${tile.tone}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <h3 className="text-[13px] font-bold text-content">
                {t("monitoreoIa.custody.title")}
              </h3>
              <p className="mb-4 text-[11px] text-content-secondary">
                {t("monitoreoIa.custody.subtitle")}
              </p>

              {summaryPending ? (
                <TabLoader label={t("monitoreoIa.panel.loading")} />
              ) : escortedList.length === 0 ? (
                <p className="py-4 text-center text-[13px] text-content-secondary">
                  {t("monitoreoIa.custody.empty")}
                </p>
              ) : (
                <ul className="space-y-3">
                  {escortedList.map(({ custody, trip, unitName }) => (
                    <li
                      key={custody.tripId}
                      className="flex items-center gap-3 rounded-xl bg-surface p-3"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-shield-check-line text-sm text-primary-500" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12.5px] font-semibold text-content">
                          {custody.title ?? trip?.title ?? t("monitoreoIa.trips.untitled")}
                        </p>
                        <p className="truncate text-[11px] text-content-secondary">
                          {custodyDetail(custody, unitName)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => navigate("/monitoreo-ia/viajes")}
                      >
                        {t("monitoreoIa.custody.view")}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-xs">
                <span className="text-content-secondary">{t("monitoreoIa.custody.scheduled")}</span>
                <span className="font-bold text-warning">
                  {panelReady ? numberFormat(panel.trips.scheduled) : "—"}
                </span>
              </div>
            </Card>
          </div>

        </div>
      </div>

      {verCasos ? <CasosAbiertosModal onClose={() => setVerCasos(false)} /> : null}
    </div>
  );
}
